/**
 * Helper central para formatação e detecção de tipo de documento (CPF/CNPJ).
 * Reutilizável em pipes, componentes e máscaras.
 * CNPJ segue o algoritmo único do Serpro (numérico e alfanumérico).
 */

import { CPFValidator } from '../validators/CPFValidator';
import { CNPJValidator } from '../validators/CNPJValidator';

export type TipoDocumento = 'CPF' | 'CNPJ' | 'UNKNOWN';

export class DocumentoHelper {

  /** Máscara para CPF (apenas dígitos) usada em formatação de exibição. */
  static readonly MASCARA_CPF = '000.000.000-00';

  /**
   * Máscara de input para CPF que aceita alfanumérico (ngx-mask 'A') com
   * um slot opcional de overflow ('9') para detectar a transição CPF→CNPJ.
   *
   * - 'A' aceita letras e dígitos: se o usuário digitar uma letra, o
   *   {@link getMascaraCpfCnpj} identifica e troca para máscara CNPJ.
   * - '9' (dígito opcional) permite que o 12.º caractere seja capturado;
   *   ao atingir length > 11, a máscara troca automaticamente para CNPJ.
   */
  static readonly MASCARA_CPF_INPUT = 'AAA.AAA.AAA-AA9';

  /**
   * Máscara para CNPJ aceitando numérico e alfanumérico (12 caracteres A–Z/0–9 + 2 DVs).
   * Estrutura: XX.XXX.XXX/XXXX-XX. No ngx-mask, 'A' = letras e dígitos.
   */
  static readonly MASCARA_CNPJ = 'AA.AAA.AAA/AAAA-00';

  /**
   * Detecta o tipo de documento pelo conteúdo (não apenas pelo tamanho).
   * - CPF: 11 dígitos e válido pelo algoritmo de CPF.
   * - CNPJ: 14 caracteres após normalize; {@link CNPJValidator.isValid} já trata CNPJ só numérico
   *   e alfanumérico com o mesmo algoritmo Serpro (não há ramo separado para cada um).
   */
  static detectarTipoDocumento(valor: string | null | undefined): TipoDocumento {
    if (valor == null || valor === '') return 'UNKNOWN';

    const apenasDigitos = (valor.match(/\d/g) || []).join('');
    const cnpjNormalizado = CNPJValidator.normalize(valor);

    if (apenasDigitos.length === 11 && CPFValidator.isValidCPF(valor)) return 'CPF';
    // Um único isValid: 14 chars (12 base A–Z/0–9 + 2 DVs), numérico ou alfanumérico.
    if (cnpjNormalizado.length === 14 && CNPJValidator.isValid(cnpjNormalizado)) return 'CNPJ';

    return 'UNKNOWN';
  }

  /**
   * Retorna a máscara de input recomendada para o valor atual.
   * Usado para campos que alternam entre CPF e CNPJ (ex.: busca por documento).
   *
   * Lógica baseada no conteúdo bruto (sem validação):
   * - Vazio/null → máscara CPF (campo começa como CPF).
   * - Contém letra → CNPJ (CPF é somente numérico).
   * - Mais de 11 caracteres → CNPJ (CPF tem no máximo 11).
   * - Caso contrário → CPF.
   */
  static getMascaraCpfCnpj(valor: string | null | undefined): string {
    if (!valor) return this.MASCARA_CPF_INPUT;

    const raw = valor.replace(/[.\-\/\s]/g, '');

    if (/[a-zA-Z]/.test(raw)) return this.MASCARA_CNPJ;
    if (raw.length > 11) return this.MASCARA_CNPJ;

    return this.MASCARA_CPF_INPUT;
  }

  /**
   * Formata um documento (CPF ou CNPJ) para exibição.
   * - 11 dígitos válidos como CPF -> 000.000.000-00
   * - 14 caracteres válidos como CNPJ (numérico ou alfanumérico) -> 00.000.000/0000-00 (ou AA.AAA.AAA/AAAA-DD)
   * - Caso contrário retorna o valor normalizado/trimmed sem alteração.
   */
  static formatarDocumento(valor: string | null | undefined): string {
    if (valor == null || valor === undefined) return '';
    const v = String(valor).trim();
    if (v === '') return '';

    const apenasDigitos = (v.match(/\d/g) || []).join('');
    const cnpjNormalizado = CNPJValidator.normalize(v);

    if (apenasDigitos.length === 11 && CPFValidator.isValidCPF(v)) {
      return CPFValidator.getFormattedCpf(v);
    }
    if (cnpjNormalizado.length === 14 && CNPJValidator.isValid(cnpjNormalizado)) {
      return CNPJValidator.format(cnpjNormalizado);
    }

    return v;
  }

  /**
   * Normaliza o valor para envio ao backend (sem máscara).
   * Para CPF: apenas dígitos (11).
   * Para CNPJ: 14 caracteres alfanuméricos sem pontuação (preserva letras).
   */
  static normalizarParaEnvio(valor: string | null | undefined, tipo: TipoDocumento): string {
    if (valor == null || valor === '') return '';
    if (tipo === 'CPF') return CPFValidator.getCPFNumbers(valor);
    if (tipo === 'CNPJ') return CNPJValidator.normalize(valor);
    return String(valor).replace(/[.\/\-\s]/g, '');
  }
}
